import { useQuery } from '@tanstack/react-query';

const INSURANCE_SERVICE_URL =
  process.env.NEXT_PUBLIC_INSURANCE_SERVICE_URL || 'http://localhost:8004';
const REPORTS_URL = `${INSURANCE_SERVICE_URL}/api/v1/reports`;

export interface DealerScorecardReasonCount {
  reason_code_id: number;
  reason_code: string;
  reason_label: string;
  count: number;
}

export interface DealerScorecardRow {
  tenant_id: string;
  total_count: number;
  submitted_count: number;
  quoted_count: number;
  converted_count: number;
  conversion_rate_pct: number;
  avg_tat_seconds: number | null;
  top_loss_reasons: DealerScorecardReasonCount[];
}

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    window.localStorage.getItem('super_admin_token') ||
    window.localStorage.getItem('access_token')
  );
}

export const dealerScorecardReportKeys = {
  all: ['dealer-scorecard-report'] as const,
  range: (dateFrom: string, dateTo: string) =>
    [...dealerScorecardReportKeys.all, dateFrom, dateTo] as const,
};

async function fetchDealerScorecardReport(
  dateFrom: string,
  dateTo: string,
): Promise<DealerScorecardRow[]> {
  const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  const response = await fetch(`${REPORTS_URL}/dealer-scorecard?${params.toString()}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to load dealer scorecard report (${response.status})`);
  }
  return response.json();
}

/** Per-dealer submitted/quoted/converted counts, conversion rate, average TAT, and top loss reasons for the Dealer Scorecard report. */
export function useDealerScorecardReport(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: dealerScorecardReportKeys.range(dateFrom, dateTo),
    queryFn: () => fetchDealerScorecardReport(dateFrom, dateTo),
    staleTime: 30_000,
  });
}
